import React from "react";
import { Link } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { apiJerseyOrderMasuk, formatApiErrorDetail } from "@/lib/api";
import { rupiah, tanggalIndo } from "@/lib/format";
import { STATUS_JERSEY } from "@/pages/JerseyDetail";
import { Loader2, Plus, RefreshCw } from "lucide-react";

// Menu "Order Masuk Jersey": orders waiting for the customer's ACC, with payment status.
const TABS = [["ORDER,SIAP_ACC", "Menunggu ACC"], ["ACC", "Sudah ACC"]];
const BAYAR = { BELUM_BAYAR: "text-red-300", DP: "text-amber-300", LUNAS: "text-emerald-300" };

export default function JerseyOrderMasuk() {
  const [tab, setTab] = React.useState(TABS[0][0]);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setRows(await apiJerseyOrderMasuk(tab)); setError(null); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [tab]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="jersey-order-masuk-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Order Masuk Jersey</h1>
            <p className="text-sm text-slate-400 mt-1">Order berstatus Order / Siap ACC beserta status pembayaran. ACC hanya bisa jika semua checklist terpenuhi.</p>
          </div>
          <Link data-testid="jersey-baru" to="/jersey/baru" className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400"><Plus size={15} /> Order Jersey baru</Link>
        </div>
        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        <div className="flex items-center gap-2">
          {TABS.map(([k, label]) => (
            <button key={k} data-testid={`jersey-tab-${k}`} onClick={() => setTab(k)} className={`rounded-md px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${tab === k ? "border-amber-500 bg-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-400"}`}>{label}</button>
          ))}
          <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-x-auto">
          {loading ? <div className="p-8 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div> : (
            <table className="w-full text-sm" data-testid="jersey-order-table">
              <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                <th className="px-4 py-3">Faktur</th><th className="px-4 py-3">Customer / tim</th><th className="px-4 py-3">PCS</th><th className="px-4 py-3">Ambil/kirim</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Bayar</th><th className="px-4 py-3">Checklist</th></tr></thead>
              <tbody>
                {rows.map((r) => {
                  const [label, cls] = STATUS_JERSEY[r.status_jersey] || [r.status_jersey, ""];
                  return (
                    <tr key={r.id} data-testid={`jersey-row-${r.nomor_faktur}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-3"><Link to={`/jersey/${r.id}`} className="font-mono text-amber-300 hover:underline">{r.nomor_faktur}</Link></td>
                      <td className="px-4 py-3">{r.customer_nama}<div className="text-xs text-slate-500">{r.tim_komunitas}</div></td>
                      <td className="px-4 py-3 font-mono">{r.total_pcs}</td>
                      <td className="px-4 py-3 text-xs">{tanggalIndo(r.tanggal_ambil_kirim)}</td>
                      <td className="px-4 py-3"><span className={`rounded-md border px-2 py-0.5 text-xs font-mono ${cls}`}>{label}</span></td>
                      <td className="px-4 py-3 text-right font-mono">{rupiah(r.total)}</td>
                      <td className={`px-4 py-3 text-xs font-mono ${BAYAR[r.status_bayar] || ""}`}>{r.status_bayar}{r.dibayar > 0 && r.sisa > 0 ? ` · sisa ${rupiah(r.sisa)}` : ""}</td>
                      <td className="px-4 py-3 text-xs">{r.status_jersey === "ACC" ? "—" : r.boleh_acc ? <span className="text-emerald-300">siap ACC</span> : <span className="text-amber-300">{r.checklist_belum} belum</span>}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Tidak ada order.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
